import classnames from 'classnames'
import { LoanOfferProductSubType, PersonalizedStatus } from 'embed/constants'
import { getContrastingColor } from 'embed/utils'
import { css } from 'emotion'
import * as React from 'react'
import { CoApplicantOfferBadge } from 'ui/multi-product/components/CoApplicantOfferBadge'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { Tooltip } from '../Tooltip'

const MoneyIcon = require('./Icons/money-2.svg')
const MoneyBagIcon = require('./Icons/money-bag.svg')
const SecuredIcon = require('./Icons/secured.svg')
const CheckScallop = require('./Icons/check-scallop.svg')
const CarIcon = require('./Icons/car.svg')

const commonStyles = require('embed/styles/results')

const styles = require('./styles')
const Info = require('./Icons/info-grey.svg')

const DEFAULT_LINE_OF_CREDIT_BANNER_TOOLTIP =
  'A line of credit is a financial product that you can borrow from at any time based on a preset limit. ' +
  'As long as your account is open and in good standing, you can borrow up to the maximum amount and pay interest only on the amount you borrow. ' +
  'As you pay it off over time, that credit will once again become available for you to borrow from.'

const DEFAULT_SECURED_OFFER_TOOLTIP_TEXT =
  'A loan that requires you to use your motor vehicle as collateral. The lender places ' +
  'a lien on the collateral until the loan is paid in full.'

const DEFAULT_PRE_QUALIFIED_OFFER_TOOLTIP_TEXT =
  'The terms “Pre-Approved” or “Pre-Qualified” presented on any offer is defined' +
  ' by the lending partner or platform and the soft credit report is pulled in' +
  ' accordance with the consumer’s written authorization. A soft pull does not' +
  ' affect your credit score. Lending partners or platforms may require a hard' +
  ' pull at the end of the process on their website and have their own selection' +
  ' criteria that is not defined by Even or TransUnion. To be clear, this is not' +
  ' a firm offer of credit.'

const DEFAULT_AUTOMOBILE_REFINANCE_OFFER_TOOLTIP_TEXT =
  'An auto refinance loan can be used to pay off your current auto loan.' +
  ' Your payments moving forward would be to your new auto refinance loan provider,' +
  ' possibly at a lower interest rate or with lower monthly payments (depending on creditworthiness).' +
  ' This could possibly save you money or make your payments fit more comfortably in your budget.' +
  ' Refinancing any loan may not always result in lower overall interest and principal payments' +
  ' and in many cases it can extend the life of the loan. Your new loan would be secured by your car' +
  ' and may be used as collateral if you don’t pay the debt.'

export const DEFAULT_PERSONAL_LOAN_OFFER_TOOLTIP_TEXT =
  'Unsecured personal loans require no collateral and can be used for a large variety of purposes, ' +
  'but may have higher interest rates than other types of loans.'

export interface BannerProps {
  bannerType?: LoanOfferProductSubType
  headerText?: string
  isResultsView?: boolean
  personalized?: boolean
  recommendedOffer?: boolean
  showCoapplicantBadge?: boolean
  shouldDisplayTransunionDisclaimer?: boolean
  tooltipText?: string
}

export const productSubTypeIconSet = {
  [LoanOfferProductSubType.AutomobileRefinance]: <CarIcon />,
  [LoanOfferProductSubType.LineOfCredit]: <MoneyIcon />,
  [PersonalizedStatus.PreApproved]: <CheckScallop />,
  [LoanOfferProductSubType.PersonalLoan]: <MoneyBagIcon />,
  [LoanOfferProductSubType.StudentLoanRefinance]: <CheckScallop />,
  [LoanOfferProductSubType.SecuredLoan]: <SecuredIcon />,
}

export const bannerText = {
  [LoanOfferProductSubType.AutomobileRefinance]: 'Personal Loans - Auto Refinance',
  [LoanOfferProductSubType.DebtRelief]: 'Special Offers',
  [LoanOfferProductSubType.LineOfCredit]: 'Line of Credit',
  [LoanOfferProductSubType.PersonalLoan]: 'Personal Loans - Unsecured',
  [LoanOfferProductSubType.SecuredLoan]: 'Personal Loans - Secured',
}

export const OfferBanner = (props: BannerProps) => {
  const {
    bannerType,
    headerText,
    isResultsView,
    personalized,
    recommendedOffer,
    shouldDisplayTransunionDisclaimer,
    showCoapplicantBadge,
    tooltipText,
  } = props

  const overlayText = {
    [LoanOfferProductSubType.AutomobileRefinance]: tooltipText || DEFAULT_AUTOMOBILE_REFINANCE_OFFER_TOOLTIP_TEXT,
    [LoanOfferProductSubType.LineOfCredit]: tooltipText || DEFAULT_LINE_OF_CREDIT_BANNER_TOOLTIP,
    [LoanOfferProductSubType.SecuredLoan]: tooltipText || DEFAULT_SECURED_OFFER_TOOLTIP_TEXT,
    [LoanOfferProductSubType.StudentLoanRefinance]: tooltipText || undefined,
    [LoanOfferProductSubType.PersonalLoan]: tooltipText || DEFAULT_PRE_QUALIFIED_OFFER_TOOLTIP_TEXT,
  }

  const shouldRender =
    isResultsView && (personalized || (bannerType && (bannerText[bannerType] || overlayText[bannerType])))
  return shouldRender ? (
    <div className={commonStyles.preApprovedSection}>
      <PreApprovedFlag
        personalized={personalized}
        headerText={headerText}
        preApprovedIcon={productSubTypeIconSet[PersonalizedStatus.PreApproved]}
        recommendedOffer={recommendedOffer}
      />
      {bannerType && (
        <div className={styles.offerBanner}>
          {productSubTypeIconSet[bannerType]}
          <span id="banner-text" className={styles.offerBannerText}>
            {bannerText[bannerType]}
          </span>
          {isResultsView && overlayText[bannerType] && (
            <Tooltip overlay={overlayText[bannerType]} placement="top">
              <Info className={commonStyles.infoTooltip} />
            </Tooltip>
          )}
          {showCoapplicantBadge && <CoApplicantOfferBadge />}
        </div>
      )}
    </div>
  ) : null
}

const PreApprovedFlag = ({ personalized, preApprovedIcon, headerText, recommendedOffer }) =>
  personalized ? (
    <ThemeConsumer>
      {({ colorSecondary, colorTextPrimary, fontFamilyPrimary }) => (
        <div className={commonStyles.preApprovedSection}>
          {personalized && (
            <div
              data-test-id="offer_flag"
              className={classnames(
                commonStyles.preApprovedFlag,
                css`
                  background-color: ${colorSecondary};
                  font-family: ${fontFamilyPrimary};
                  color: ${getContrastingColor(colorSecondary, { black: colorTextPrimary })};
                  svg path {
                    fill: ${getContrastingColor(colorSecondary, { black: colorTextPrimary })};
                  }
                  &::after {
                    border-color: ${colorSecondary};
                    border-right-color: transparent;
                  }
                `,
              )}
            >
              <div className={styles.offerBanner}>
                {productSubTypeIconSet[PersonalizedStatus.PreApproved]}
                <span className={styles.offerBannerText}>
                  {headerText} {recommendedOffer && ' - Recommended'}
                </span>
              </div>
            </div>
          )}
          {!personalized && (
            <div className={styles.offerBanner}>
              {preApprovedIcon}
              <span className={styles.offerBannerText}>
                {headerText} {recommendedOffer && ' - Recommended'}
              </span>
            </div>
          )}
        </div>
      )}
    </ThemeConsumer>
  ) : null
